import {useEffect, useState} from "react";
import {useApi} from "../api";
import './Duplicates.scss'

type DupeGroup = {
    sum: string
    files: VideoFile[]
}

export default () => {
    const api = useApi()

    const [groups,  setGroups]  = useState<DupeGroup[] | null>(null)
    const [loading, setLoading] = useState(true)
    const [keepers, setKeepers] = useState<Record<string, string>>({})   // sum → hash to keep
    const [busy,    setBusy]    = useState<Record<string, boolean>>({})  // sum → loading

    const load = async () => {
        setLoading(true)
        const data = await api.getDuplicates().catch(() => [])
        setGroups(data)
        const initial: Record<string, string> = {}
        for (const g of data) {
            initial[g.sum] = g.files[0]?.hash || ''
        }
        setKeepers(initial)
        setLoading(false)
    }

    useEffect(() => { load() }, [])

    const keeperFor = (sum: string) => keepers[sum] || ''

    const setKeeper = (sum: string, hash: string) => {
        setKeepers(k => ({ ...k, [sum]: hash }))
    }

    const deleteGroup = async (sum: string) => {
        const group = groups?.find(g => g.sum === sum)
        if (!group) return
        const toDelete = group.files.filter(f => f.hash !== keeperFor(sum)).map(f => f.hash)
        if (toDelete.length === 0) return
        setBusy(b => ({ ...b, [sum]: true }))
        await api.removeFiles(toDelete).catch(() => {})
        setBusy(b => ({ ...b, [sum]: false }))
        setGroups(gs => gs?.filter(g => g.sum !== sum) ?? null)
    }

    const deleteAll = async () => {
        if (!groups || groups.length === 0) return
        const toDelete: string[] = []
        for (const g of groups) {
            g.files.filter(f => f.hash !== keeperFor(g.sum)).forEach(f => toDelete.push(f.hash))
        }
        if (toDelete.length === 0) return
        if (!window.confirm(`Delete ${toDelete.length} duplicate file${toDelete.length !== 1 ? 's' : ''}?`)) return
        setBusy(() => Object.fromEntries(groups.map(g => [g.sum, true])))
        await api.removeFiles(toDelete).catch(() => {})
        setGroups([])
    }

    const totalDupes = groups?.reduce((n, g) => n + g.files.length - 1, 0) ?? 0

    return (
        <div className="dupes-view">
            <div className="dupes-header">
                <h2 className="dupes-title">Duplicate files</h2>
                {!loading && groups !== null && (
                    <div className="dupes-summary">
                        {groups.length === 0
                            ? 'No duplicates found.'
                            : <>
                                <span>{groups.length} group{groups.length !== 1 ? 's' : ''} · {totalDupes} extra cop{totalDupes !== 1 ? 'ies' : 'y'}</span>
                                {totalDupes > 0 && (
                                    <button className="dupes-clean-all" onClick={deleteAll}>
                                        Delete all duplicates
                                    </button>
                                )}
                            </>
                        }
                    </div>
                )}
            </div>

            {loading && <div className="dupes-loading">Scanning…</div>}

            <div className="dupes-groups">
                {groups?.map(group => (
                    <div key={group.sum} className={'dupes-group' + (busy[group.sum] ? ' dupes-group--busy' : '')}>
                        <div className="dupes-group-header">
                            <span className="dupes-group-sum" title={group.sum}>{group.sum.slice(0, 12)}…</span>
                            <span className="dupes-group-count">{group.files.length} copies</span>
                            <button
                                className="dupes-clean-btn"
                                onClick={() => deleteGroup(group.sum)}
                                disabled={busy[group.sum]}
                            >
                                {busy[group.sum] ? 'Deleting…' : `Delete ${group.files.length - 1} duplicate${group.files.length - 1 !== 1 ? 's' : ''}`}
                            </button>
                        </div>

                        <div className="dupes-files">
                            {group.files.map(file => {
                                const isKeeper = keeperFor(group.sum) === file.hash
                                return (
                                    <div
                                        key={file.hash}
                                        className={'dupes-file' + (isKeeper ? ' dupes-file--keep' : ' dupes-file--delete')}
                                        onClick={() => setKeeper(group.sum, file.hash)}
                                        title={isKeeper ? 'Will be kept' : 'Click to keep this one instead'}
                                    >
                                        <div className="dupes-file-badge">
                                            {isKeeper ? '✓ keep' : '✕ delete'}
                                        </div>
                                        <img
                                            className="dupes-thumb"
                                            src={file.paths.thumbnail}
                                            alt=""
                                            loading="lazy"
                                        />
                                        <div className="dupes-file-info">
                                            <span className="dupes-file-name" title={file.name}>{file.name}</span>
                                            <span className="dupes-file-dir" title={file.directory?.path}>
                                                {file.directory?.name}
                                            </span>
                                            <span className="dupes-file-path" title={file.path}>{file.path}</span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}
